// App shell : routage minimal maison (pas de react-router) — état `vue` synchronisé
// au hash `#/t1` `#/t2`, T2 par défaut (D2). Navigation PERMANENTE LOT 7 §1 :
// [ Prise de commande ] [ Production · N commandes actives ] sur les deux écrans
// (N = non terminales, via useFileCommandes/INV-C). Codes T1/T2 internes
// uniquement (routes inchangées). `#/reset-demo` (LOT 7c §5) = route NON LIÉE,
// rendue SANS le shell de service ni aucune requête live (la base est en cours
// de suppression). Boutons MAISON (aucune animation — garde-fou Production).
import { useEffect, useState } from 'react';
import { useFileCommandes } from '@/hooks/useFileCommandes';
import { pluriel } from '@/lib/pluriel';
import { T1Screen } from '@/screens/t1/T1Screen';
import { T2Screen } from '@/screens/t2/T2Screen';
import { ResetDemoScreen } from '@/screens/reset/ResetDemoScreen';

export type Vue = 't1' | 't2' | 'reset';

function vueFromHash(): Vue {
  // T2 défaut : `#/t1` bascule en T1 ; `#/reset-demo` = route NON LIÉE (jamais
  // exposée dans l'UI de service) ; tout le reste retombe sur T2.
  const hash = globalThis.location?.hash;
  if (hash === '#/t1') return 't1';
  if (hash === '#/reset-demo') return 'reset';
  return 't2';
}

// Shell de service (nav permanente + écran actif). Les requêtes live (compte N)
// vivent ICI, jamais montées sur la route reset.
function ServiceShell({
  vue,
  aller,
}: {
  vue: 't1' | 't2';
  aller: (v: 't1' | 't2') => void;
}) {
  // N commandes actives = non terminales (getCommandesEnFile exclut
  // cloturee/annulee ; prete incluse — elle attend son retrait).
  const { commandes } = useFileCommandes();

  const navBtn = (actif: boolean): string =>
    `min-h-[80px] flex-1 rounded-xl border-2 border-ardoise px-4 font-jost text-xl ${
      actif ? 'bg-ardoise text-creme' : 'bg-creme text-ardoise'
    }`;

  return (
    <div className="min-h-screen bg-creme font-jost text-ardoise">
      <header className="flex items-center gap-3 p-4">
        <span className="font-cormorant text-2xl italic">Tablier</span>
        <nav aria-label="Navigation principale" className="flex flex-1 gap-3">
          <button
            type="button"
            data-testid="nav-prise"
            onClick={() => aller('t1')}
            aria-current={vue === 't1' ? 'page' : undefined}
            className={navBtn(vue === 't1')}
          >
            Prise de commande
          </button>
          <button
            type="button"
            data-testid="nav-production"
            onClick={() => aller('t2')}
            aria-current={vue === 't2' ? 'page' : undefined}
            className={navBtn(vue === 't2')}
          >
            Production · {commandes.length}{' '}
            {pluriel(commandes.length, 'commande')}{' '}
            {pluriel(commandes.length, 'active')}
          </button>
        </nav>
      </header>
      <main>{vue === 't1' ? <T1Screen /> : <T2Screen />}</main>
    </div>
  );
}

export function App() {
  const [vue, setVue] = useState<Vue>(vueFromHash);

  useEffect(() => {
    const onHash = (): void => setVue(vueFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const aller = (next: 't1' | 't2'): void => {
    window.location.hash = next === 't1' ? '#/t1' : '#/t2';
    setVue(next); // maj immédiate (l'événement hashchange n'est pas garanti synchrone)
  };

  // Écran reset démo : plein écran, SANS le shell de service (route non liée).
  if (vue === 'reset') {
    return <ResetDemoScreen />;
  }

  return <ServiceShell vue={vue} aller={aller} />;
}
